import React, { useState, useRef, useEffect, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PaperAirplaneIcon, StopIcon } from '@heroicons/react/24/solid';
import { PlusIcon, ChevronRightIcon, DocumentPlusIcon, Squares2X2Icon, CpuChipIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import AttachmentIcon from '../icons/AttachmentIcon';
import CoworkAttachmentCard from './CoworkAttachmentCard';
import { isCoworkImagePath } from './coworkAttachmentPresentation';
import ModelSelector from '../ModelSelector';
import RelayCoworkPermissionModeSelector from './RelayCoworkPermissionModeSelector';
import CloudPermissionPolicy from './CloudPermissionPolicy';
import ExecutionEnvPopover from './ExecutionEnvPopover';
import CwdPickerModal from '../ui/CwdPickerModal';
import { ActiveSkillBadge } from '../skills';
import SkillsPopover from '../skills/SkillsPopover';
import { i18nService } from '../../services/i18n';
import { skillService } from '../../services/skill';
import { buildCloudEnvOptions, findCloudEnvOption, resolveSessionCloudEnv } from '../../utils/sandboxEnv';
import { RootState } from '../../store';
import { authService } from '../../services/auth';
import { conversationDraftKey, conversationDrafts } from '../../services/conversationDraft';
import { setSkills, toggleActiveSkill } from '../../store/slices/skillSlice';
import { Skill } from '../../types/skill';
import type { SandboxProfile, SandboxImage } from '../../types/cowork';
import { relayService, getTargetClient, type RelayClientInfo } from '../../services/relay';
import { scenarioService, type MyScenario } from '../../services/scenario';
import { BrowserAsrSession, composeAsrText, type BrowserAsrState } from '../../services/asr';
import ScenarioAppIcon from '../scenarios/ScenarioAppIcon';
import ScenarioDetailDialog from '../scenarios/ScenarioDetailDialog';
import { uploadBrowserHostInput, BROWSER_HOST_INPUT_MAX_BYTES, isHostInputRejection } from '../../services/hostInputTransfer';
import {
  buildHostInputAttachmentToken,
  buildHostInputDraftTurnId,
  HOST_INPUT_DRAFT_SESSION_ID,
} from '../../services/hostInputAttachmentToken';
import { randomUuid } from '../../utils/randomUuid';

/** WEB-08: the Host rejected the file's content (e.g. compression bomb); never fall back. */
const HOST_INPUT_REJECTED = '\u0000host-input-rejected';

export type CoworkAttachment = {
  path: string;
  name: string;
  /** 字节数（入列时从 File 捕获，仅用于附件卡片展示）。 */
  size?: number;
  /** Kept only for direct Web → Desktop relay uploads. */
  file?: File;
};

const INPUT_FILE_LABEL = '输入文件';

const getFileNameFromPath = (path: string): string => {
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || path;
};

const getSkillDirectoryFromPath = (skillPath: string): string => {
  const normalized = skillPath.trim().replace(/\\/g, '/');
  return normalized.replace(/\/SKILL\.md$/i, '') || normalized;
};

export const buildInlinedSkillPrompt = (skill: Skill): string => {
  const skillDirectory = getSkillDirectoryFromPath(skill.skillPath);
  const source = skill.source || 'user';
  const lines = [
    `## Skill: ${skill.name}`,
    '<skill_context>',
    `  <location>${skill.skillPath}</location>`,
    `  <directory>${skillDirectory}</directory>`,
    `  <source>${source}</source>`,
    '  <path_rules>',
    '    根据 <directory> 解析此技能中的相对文件引用。',
    '    不要假设技能位于当前工作空间目录下。',
    '  </path_rules>',
  ];
  // skill-creator 技能：指定沙箱中生成技能的存放路径
  if (skill.id === 'skill-creator' || skill.id === 'system:skill-creator') {
    lines.push('  <output_path>/workspace/skills/private-skill</output_path>');
    lines.push('  <output_rules>创建新技能时，将所有技能文件保存到 <output_path>/<skill-id>/ 目录下。创建脚本文件（.sh、.py 等）后，执行 `chmod +x` 确保其可执行。</output_rules>');
  }
  lines.push('</skill_context>');
  lines.push('');
  if (skill.prompt) {
    lines.push(skill.prompt);
  } else {
    // 系统精选技能在前端没有 prompt 内容，指示模型从沙箱路径读取
    lines.push('使用 Read 工具读取 <location> 路径下的 SKILL.md 文件，然后遵照其中的指令执行。');
  }
  return lines.join('\n');
};

export interface CoworkPromptInputRef {
  /** 设置输入框值 */
  setValue: (value: string) => void;
  /** 聚焦输入框 */
  focus: () => void;
  /** 通过外部拖拽添加文件 */
  addFiles: (files: FileList | File[]) => void;
}

interface CoworkPromptInputProps {
  onSubmit: (prompt: string, skillPrompt?: string, attachments?: CoworkAttachment[]) => void | boolean | Promise<void | boolean>;
  onStop?: () => void;
  isStreaming?: boolean;
  placeholder?: string;
  disabled?: boolean;
  size?: 'normal' | 'large';
  showSandboxSelector?: boolean;
  sandboxProfiles?: SandboxProfile[];
  selectedSandboxProfileId?: string | null;
  onSandboxProfileChange?: (id: string | null) => void;
  onManageSandboxes?: () => void;
  showModelSelector?: boolean;
  onManageSkills?: () => void;
  sandboxImages?: SandboxImage[];
  selectedSandboxImageId?: string | null;
  onSandboxImageChange?: (id: string | null) => void;
  onManageSandboxImages?: () => void;
  /** When true, sandbox selectors are shown but greyed-out / non-interactive */
  sandboxDisabled?: boolean;
  cwd?: string;
  executionMode?: string;
  sessionId?: string;
  /** `sessionId` is a local staging id for a chat that has no Host session yet. */
  sessionIsDraft?: boolean;
  /** Currently selected desktop client ID (mutually exclusive with sandbox profile) */
  selectedDesktopClientId?: string | null;
  /** Cached device name for the selected desktop client (avoids stale relay lookup) */
  selectedDesktopClientName?: string | null;
  /** Called when user selects/deselects a desktop client */
  onDesktopClientChange?: (clientId: string | null) => void;
  /** 桌面端会话的工作目录（null = 用桌面端默认目录）。仅新建会话时可选。 */
  desktopCwd?: string | null;
  /** 工作目录变更回调；传 null 表示回到「默认目录」。 */
  onDesktopCwdChange?: (cwd: string | null) => void;
  /** 新建任务输入框是否跟随「协作/快速」档位的交互规则。 */
  showPromptModeToggle?: boolean;
  /** 当前激活的场景应用（协作模式显示在场景选择器上）。 */
  activeScenario?: MyScenario | null;
  /** 从输入框场景选择器选中场景（走 App 的技能装配确认流程）。 */
  onSelectScenario?: (scenario: MyScenario) => void;
}

/**
 * 场景选择器（协作模式专用）：不离开协作视图即可挑选场景应用。
 * 选择动作交给上层（App.handleStartScenarioChat）——沿用技能装配确认与进入流程。
 */
const ScenarioPickerPanel: React.FC<{
  activeScenario?: MyScenario | null;
  onSelectScenario: (scenario: MyScenario) => void;
  onClose: () => void;
}> = ({ activeScenario, onSelectScenario, onClose }) => {
  const [query, setQuery] = useState('');
  const [scenarios, setScenarios] = useState<MyScenario[]>(scenarioService.getScenarios());
  const [loading, setLoading] = useState(scenarioService.isLoading);

  useEffect(() => scenarioService.subscribe(() => {
    setScenarios(scenarioService.getScenarios());
    setLoading(scenarioService.isLoading);
  }), []);

  useEffect(() => {
    if (scenarioService.getScenarios().length === 0 && !scenarioService.isLoading) {
      void scenarioService.loadScenarios();
    }
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleScenarios = normalizedQuery
    ? scenarios.filter((scenario) => [
        scenario.name,
        scenario.description,
        scenario.expert_name,
        scenario.group_name,
        scenario.tags,
      ].some((field) => (field || '').toLowerCase().includes(normalizedQuery)))
    : scenarios;

  return (
    <div className="w-80 overflow-hidden rounded-xl border border-claude-border bg-white shadow-xl dark:border-claude-darkBorder dark:bg-claude-darkSurface">
      <div className="border-b border-claude-borderLight p-2 dark:border-claude-darkBorderLight">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={i18nService.t('scenariosSearchPlaceholder')}
          className="h-8 w-full rounded-lg border border-claude-border bg-transparent px-2.5 text-sm text-claude-text outline-none placeholder:text-claude-textSecondary/60 focus:border-claude-accent/60 dark:border-claude-darkBorder dark:text-claude-darkText"
          autoFocus
        />
      </div>
      <div className="max-h-72 overflow-y-auto p-1.5">
        {loading && scenarios.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {i18nService.t('loading')}
          </div>
        ) : scenarioService.loadFailed ? (
          <div role="alert" className="space-y-2 px-3 py-5 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
            <p>{i18nService.t('scenariosLoadFailed')}</p>
            <button type="button" onClick={() => void scenarioService.loadScenarios()} className="rounded border px-3 py-1">{i18nService.t('retry')}</button>
          </div>
        ) : scenarioService.authFailed ? (
          <p className="px-3 py-5 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('scenariosAuthFailedHint')}</p>
        ) : visibleScenarios.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {i18nService.t('scenariosNoResults')}
          </div>
        ) : visibleScenarios.map((scenario) => {
          const active = activeScenario?.id === scenario.id;
          return (
            <button
              key={scenario.id}
              type="button"
              onClick={() => {
                onClose();
                onSelectScenario(scenario);
              }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                active
                  ? 'bg-claude-accent/10'
                  : 'hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover'
              }`}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-claude-surfaceMuted dark:bg-claude-darkSurfaceHover">
                <ScenarioAppIcon scenario={scenario} size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-claude-text dark:text-claude-darkText">
                  {scenario.name}
                </div>
                <div className="truncate text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                  {scenario.expert_name?.trim() || scenario.group_name}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ScopedPromptInput = React.forwardRef<CoworkPromptInputRef, CoworkPromptInputProps & { draftKey: string }>(
  (props, ref) => {
    const {
      draftKey,
      onSubmit,
      onStop,
      isStreaming = false,
      placeholder = 'Enter your task...',
      disabled = false,
      size = 'normal',
      showSandboxSelector = false,
      sandboxProfiles = [],
      selectedSandboxProfileId = null,
      onSandboxProfileChange,
      onManageSandboxes,
      showModelSelector = false,
      showPromptModeToggle = false,
      activeScenario = null,
      onSelectScenario,
      onManageSkills,
      sandboxImages = [],
      selectedSandboxImageId = null,
      onSandboxImageChange,
      onManageSandboxImages,
      sandboxDisabled = false,
      cwd,
      executionMode,
      sessionId,
      sessionIsDraft,
      selectedDesktopClientId = null,
      selectedDesktopClientName = null,
      onDesktopClientChange,
      desktopCwd = null,
      onDesktopCwdChange,
    } = props;
    const dispatch = useDispatch();
    const newTaskPromptMode = useSelector((state: RootState) => state.cowork.newTaskPromptMode);
    // 仅新任务输入框(showPromptModeToggle)受档位影响;会话内输入框沿用会话固化档位
    const isQuickComposer = showPromptModeToggle && newTaskPromptMode === 'quick';
    // 「+」统一工具菜单(对齐桌面端):收纳 添加文件/场景/技能
    const [plusOpen, setPlusOpen] = useState(false);
    const [plusPanel, setPlusPanel] = useState<null | 'scenario' | 'skills'>(null);
    const [previewScenario, setPreviewScenario] = useState<MyScenario | null>(null);
    const plusRef = useRef<HTMLDivElement>(null);
    const plusBtnRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      if (!plusOpen && plusPanel !== 'scenario') return;
      const onDocClick = (event: MouseEvent) => {
        if (plusRef.current && !plusRef.current.contains(event.target as Node)) {
          setPlusOpen(false);
          if (plusPanel === 'scenario') setPlusPanel(null);
        }
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [plusOpen, plusPanel]);
    const [value, setValue] = useState(() => conversationDrafts.read(draftKey).text);
    const [asrState, setAsrState] = useState<BrowserAsrState>('idle');
    const [attachments, setAttachments] = useState<CoworkAttachment[]>(() => conversationDrafts.read(draftKey).attachments);
    const submittingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [lostAttachments, setLostAttachments] = useState(() => conversationDrafts.read(draftKey).lostAttachments);
    const previousPromptModeRef = useRef(newTaskPromptMode);

    useEffect(() => {
      const previousMode = previousPromptModeRef.current;
      previousPromptModeRef.current = newTaskPromptMode;
      if (showPromptModeToggle && previousMode !== 'quick' && newTaskPromptMode === 'quick') {
        setAttachments([]);
      }
    }, [newTaskPromptMode, showPromptModeToggle]);
    const [showSandboxMenu, setShowSandboxMenu] = useState(false);
    const [showCwdPicker, setShowCwdPicker] = useState(false);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const [relayClients, setRelayClients] = useState<RelayClientInfo[]>([]);
    const [relayReady, setRelayReady] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const sandboxButtonRef = useRef<HTMLButtonElement>(null);
    const dragDepthRef = useRef(0);
    const asrSessionRef = useRef<BrowserAsrSession | null>(null);
    const asrBaseValueRef = useRef('');
    const asrFinalSegmentsRef = useRef<string[]>([]);

    // 执行环境只剩一层：列的是沙箱模板，运行环境（profile）跟着模板走。
    // 摊平规则与可见性过滤都在 utils/sandboxEnv，定时任务表单用的是同一份。
    const cloudEnvOptions = useMemo(
      () => buildCloudEnvOptions(sandboxProfiles, sandboxImages),
      [sandboxProfiles, sandboxImages],
    );
    // 已锁定环境的会话（sandboxDisabled）只按会话自己的模板精确显示；模板被删/停用时显示
    // 「不可用」，不能像新建任务那样回退到同运行环境的第一个模板（曾把 v3 会话显示成 721-Sandbox）。
    const lockedCloudEnv = sandboxDisabled && !selectedDesktopClientId
      ? resolveSessionCloudEnv(cloudEnvOptions, selectedSandboxProfileId ?? null, selectedSandboxImageId ?? null)
      : null;
    const activeCloudEnv = selectedDesktopClientId
      ? null
      : lockedCloudEnv
        ? lockedCloudEnv.option
        : findCloudEnvOption(cloudEnvOptions, selectedSandboxProfileId ?? null, selectedSandboxImageId ?? null);
    const cloudEnvChipLabel = lockedCloudEnv?.unavailable && cloudEnvOptions.length > 0
      ? i18nService.t('sandboxEnvUnavailable')
      : (activeCloudEnv?.label ?? i18nService.t('sandboxProfileSelectTitle'));

  const activeSkillIds = useSelector((state: RootState) => state.skill.activeSkillIds);
  const skills = useSelector((state: RootState) => state.skill.skills);

  const isLarge = size === 'large';
  const minHeight = isLarge ? 38 : 24;
  const maxHeight = isLarge ? 200 : 200;

  // Load skills on mount
  useEffect(() => {
    const loadSkills = async () => {
      const loadedSkills = await skillService.loadSkills();
      dispatch(setSkills(loadedSkills));
    };
    loadSkills();
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = skillService.onSkillsChanged(async () => {
      const loadedSkills = await skillService.loadSkills();
      dispatch(setSkills(loadedSkills));
    });
    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
    }
  }, [value, minHeight, maxHeight]);

  // Fetch relay clients when sandbox menu opens OR when a desktop client is already selected
  // (so the button can display the client device name even when disabled)
  useEffect(() => {
    if (!showSandboxMenu && !selectedDesktopClientId) return;
    setRelayReady(false);
    relayService.ensureConnected()
      .then(() => {
        setRelayClients(relayService.getAllClients());
        setRelayReady(true);
      })
      .catch(err => {
        console.warn('[EnvSelector] Failed to fetch clients:', err);
        setRelayReady(true); // connection failed → treat as ready (no clients)
      });
    const unsub = relayService.subscribe(() => {
      setRelayClients(relayService.getAllClients());
      setRelayReady(true);
    });
    return unsub;
  }, [showSandboxMenu, selectedDesktopClientId]);

  const desktopRelayClients = useMemo(
    () => relayClients.filter((client) => client.platform !== 'web' && client.online),
    [relayClients],
  );

  // 没有命中任何可选项时落到第一个，否则 sandboxReady 恒为 false、发送按钮一直禁用。
  // 这同时纠正 CoworkView 给的默认值：它取的是完整目录的第一个模板，可能归属已停用的
  // 运行环境、或与后端类型不匹配，带着这种模板去建会话必然失败。
  const cloudEnvKey = cloudEnvOptions.map((option) => option.key).join(',');
  const activeCloudEnvKey = activeCloudEnv?.key ?? '';
  useEffect(() => {
    if (!showSandboxSelector || sandboxDisabled || selectedDesktopClientId) return;
    if (activeCloudEnvKey || cloudEnvOptions.length === 0) return;
    const first = cloudEnvOptions[0];
    onSandboxProfileChange?.(first.profileId);
    onSandboxImageChange?.(first.imageId);
  }, [
    showSandboxSelector, sandboxDisabled, selectedDesktopClientId,
    cloudEnvKey, activeCloudEnvKey, onSandboxProfileChange, onSandboxImageChange,
  ]);

  const attachmentPreviews = useMemo(() => {
    const previews: Record<string, string> = {};
    for (const attachment of attachments) {
      if (attachment.file?.type.startsWith('image/')) {
        previews[attachment.path] = URL.createObjectURL(attachment.file);
      }
    }
    return previews;
  }, [attachments]);

  useEffect(() => () => {
    for (const previewUrl of Object.values(attachmentPreviews)) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [attachmentPreviews]);

  useEffect(() => {
    const handleFocusInput = (event: Event) => {
      const detail = (event as CustomEvent<{ clear?: boolean; prompt?: string }>).detail;
      const shouldClear = detail?.clear ?? true;
      if (detail?.prompt) {
        // 预填提示词
        setValue(detail.prompt);
      } else if (shouldClear) {
        setValue('');
        setAttachments([]);
      }
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    };
    window.addEventListener('cowork:focus-input', handleFocusInput);
    return () => {
      window.removeEventListener('cowork:focus-input', handleFocusInput);
    };
  }, []);

  // 空白区域保留“直接输入”体验；已有按钮、导航、菜单等焦点属于用户，不得抢回。
  useEffect(() => {
    let frame = 0;
    /** 延迟到浏览器完成本次焦点移动后检查，避免 click/keydown 的旧快照覆盖新焦点。 */
    const restoreFocus = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = document.activeElement;
        if (disabled || window.getSelection()?.toString()) return;
        if (active && active !== document.body && active !== document.documentElement) return;
        textareaRef.current?.focus({ preventScroll: true });
      });
    };
    /** 已被组件处理的快捷键，以及修饰键组合，均不触发输入框自动聚焦。 */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.altKey) restoreFocus();
    };
    window.addEventListener('focus', restoreFocus);
    document.addEventListener('click', restoreFocus);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('focus', restoreFocus);
      document.removeEventListener('click', restoreFocus);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled]);

  useEffect(() => {
    // ASR 增量仅作为输入框预览，完成后再一次性写入草稿，避免半句话被持久化。
    if (asrSessionRef.current && asrState !== 'idle') return;
    conversationDrafts.write(draftKey, value, attachments);
  }, [value, attachments, draftKey, asrState]);

  useEffect(() => conversationDrafts.subscribe((key) => {
    if (key !== draftKey) return;
    const draft = conversationDrafts.read(key);
    setValue(draft.text);
    setAttachments(draft.attachments);
    setLostAttachments(draft.lostAttachments);
  }), [draftKey]);

  const cancelAsr = useCallback(() => {
    const session = asrSessionRef.current;
    if (!session) return;
    asrSessionRef.current = null;
    session.cancel();
    const confirmedValue = composeAsrText(
      asrBaseValueRef.current,
      asrFinalSegmentsRef.current,
      '',
    );
    setValue(confirmedValue);

    setAsrState('idle');
  }, [dispatch]);

  const handleAsrClick = useCallback(() => {
    if (asrState === 'recording') {
      asrSessionRef.current?.finish();
      return;
    }
    if (asrState !== 'idle' || disabled || isStreaming) return;

    asrBaseValueRef.current = value;
    asrFinalSegmentsRef.current = [];
    let partialText = '';
    const session = new BrowserAsrSession({
      onStateChange: (state) => {
        if (asrSessionRef.current === session) setAsrState(state);
      },
      onPartial: (text) => {
        if (asrSessionRef.current !== session) return;
        partialText = text;
        setValue(composeAsrText(asrBaseValueRef.current, asrFinalSegmentsRef.current, partialText));
      },
      onFinal: (text) => {
        if (asrSessionRef.current !== session) return;
        asrFinalSegmentsRef.current.push(text);
        partialText = '';
        setValue(composeAsrText(asrBaseValueRef.current, asrFinalSegmentsRef.current, ''));
      },
      onCompleted: () => {
        if (asrSessionRef.current !== session) return;
        const finalValue = composeAsrText(
          asrBaseValueRef.current,
          asrFinalSegmentsRef.current,
          '',
        );
        asrSessionRef.current = null;
        setAsrState('idle');
        setValue(finalValue);

        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      onError: (error) => {
        if (asrSessionRef.current !== session) return;
        const confirmedValue = composeAsrText(
          asrBaseValueRef.current,
          asrFinalSegmentsRef.current,
          '',
        );
        asrSessionRef.current = null;
        setAsrState('idle');
        setValue(confirmedValue);

        window.dispatchEvent(new CustomEvent('app:showToast', { detail: error.message }));
      },
    });
    asrSessionRef.current = session;
    void session.start().catch(() => undefined);
  }, [asrState, disabled, dispatch, isStreaming, value]);

  const handleValueChange = useCallback((newValue: string) => {
    if (asrSessionRef.current) cancelAsr();
    setValue(newValue);
  }, [cancelAsr]);

  useEffect(() => {
    if ((disabled || isStreaming) && asrSessionRef.current) cancelAsr();
  }, [cancelAsr, disabled, isStreaming]);

  useEffect(() => () => {
    const session = asrSessionRef.current;
    asrSessionRef.current = null;
    session?.cancel();
  }, [sessionId]);

  const handleSubmit = useCallback(async () => {
    const trimmedValue = value.trim();
    if ((!trimmedValue && attachments.length === 0) || disabled || submittingRef.current || asrState !== 'idle') return;

    // Get active skills prompts and combine them
    const activeSkills = activeSkillIds
      .map(id => skills.find(s => s.id === id))
      .filter((s): s is Skill => s !== undefined);
    const skillPrompt = activeSkills.length > 0
      ? activeSkills.map(buildInlinedSkillPrompt).join('\n\n')
      : undefined;

    // 服务端确认发起或入队后再清空；迟到响应不能清掉用户继续输入的内容。
    submittingRef.current = true;
    setSubmitting(true);
    conversationDrafts.write(draftKey, value, attachments);
    try {
      const attachmentLines = attachments.map(attachment => `${INPUT_FILE_LABEL}: ${attachment.path}`).join('\n');
      const finalPrompt = selectedDesktopClientId ? trimmedValue : [trimmedValue, attachmentLines].filter(Boolean).join('\n\n');
      const accepted = await onSubmit(finalPrompt, skillPrompt, selectedDesktopClientId ? attachments : undefined);
      if (accepted !== false) conversationDrafts.clearIfUnchanged(draftKey, value, attachments);
    } catch (error) {
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: error instanceof Error ? error.message : i18nService.t('coworkDraftSendFailed'),
      }));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [value, disabled, onSubmit, activeSkillIds, skills, attachments, selectedDesktopClientId, asrState, draftKey]);

  const handleSelectSkill = useCallback((skill: Skill) => {
    dispatch(toggleActiveSkill(skill.id));
  }, [dispatch]);

  const handleManageSkills = useCallback(() => {
    if (onManageSkills) {
      onManageSkills();
    }
  }, [onManageSkills]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to submit, Shift+Enter for new line
    const isComposing = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
    if (event.key === 'Enter' && !event.shiftKey && !isComposing && !disabled && asrState === 'idle') {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleStopClick = () => {
    if (onStop) {
      onStop();
    }
  };

  const containerClass = isLarge
    ? 'relative min-w-0 max-w-full overflow-visible rounded-2xl bg-white transition-shadow focus-within:shadow-[0_8px_24px_rgba(15,23,42,0.07)] dark:bg-claude-darkSurface'
    : 'relative flex items-end gap-2 p-3 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface';

  const textareaClass = isLarge
    ? `w-full resize-none bg-transparent px-4 pb-1.5 pt-2 text-[15px] leading-6 text-claude-text placeholder:text-claude-textSecondary/55 focus:outline-none dark:text-claude-darkText dark:placeholder:text-claude-darkTextSecondary/55 min-h-[${minHeight}px] max-h-[${maxHeight}px]`
    : 'flex-1 resize-none bg-transparent dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none text-sm leading-relaxed min-h-[24px] max-h-[200px]';

  const addAttachment = useCallback((path: string, file?: File) => {
    if (!path) return;
    setAttachments((prev) => {
      if (prev.some((attachment) => attachment.path === path)) {
        return prev;
      }
      return [...prev, { path, name: file?.name || getFileNameFromPath(path), size: file?.size, file }];
    });
  }, []);

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to read file'));
          return;
        }
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const getNativeFilePath = useCallback((file: File): string | null => {
    const maybePath = (file as File & { path?: string }).path;
    if (typeof maybePath === 'string' && maybePath.trim()) {
      return maybePath;
    }
    return null;
  }, []);

  const saveInlineFile = useCallback(async (file: File): Promise<string | null> => {
    try {
      const dataBase64 = await fileToBase64(file);
      if (!dataBase64) {
        return null;
      }
      const result = await window.electron.dialog.saveInlineFile({
        dataBase64,
        fileName: file.name,
        mimeType: file.type,
        cwd: cwd || '',
        executionMode,
        sessionId,
      });
      if (result.success && result.path) {
        return result.path;
      }
      return null;
    } catch (error) {
      console.error('Failed to save inline file:', error);
      return null;
    }
  }, [cwd, fileToBase64, executionMode, sessionId]);

  // WEB-08: sandbox sessions upload through the chunked /api/inputs protocol and
  // reference the committed input by opaque token; the Host materializes it into
  // the sandbox attachment area (new chats use the draft scope). Any failure (type not
  // on the Host allowlist, oversize, route unavailable) falls back to legacy upload.
  const uploadHostInputAttachment = useCallback(async (file: File): Promise<string | null> => {
    if (executionMode !== 'sandbox' || !file.type || file.size < 1
      || file.size > BROWSER_HOST_INPUT_MAX_BYTES) {
      return null;
    }
    try {
      // New chat (no session yet): upload into the per-user draft scope; the Host
      // binds it to the session created by the first send.
      const hostSessionId = sessionIsDraft || !sessionId ? HOST_INPUT_DRAFT_SESSION_ID : sessionId;
      const turnId = hostSessionId === HOST_INPUT_DRAFT_SESSION_ID
        ? buildHostInputDraftTurnId(randomUuid())
        : `composer-${randomUuid()}`;
      const result = await uploadBrowserHostInput({ file, sessionId: hostSessionId, turnId });
      return buildHostInputAttachmentToken({ turnId, inputRef: result.inputRef, fileName: result.fileName });
    } catch (error) {
      if (isHostInputRejection(error)) {
        window.dispatchEvent(new CustomEvent('app:showToast', { detail: `${file.name}：${error.message}` }));
        return HOST_INPUT_REJECTED;
      }
      console.warn('[CoworkPromptInput] /api/inputs upload failed, using legacy upload:', error);
      return null;
    }
  }, [executionMode, sessionId, sessionIsDraft]);

  const handleIncomingFiles = useCallback(async (fileList: FileList | File[]) => {
    if (disabled || isStreaming) return;
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    for (const file of files) {
      if (selectedDesktopClientId) {
        if (file.size > 20 * 1024 * 1024) {
          window.dispatchEvent(new CustomEvent('app:showToast', {
            detail: `附件 ${file.name} 超过 20MB 限制`,
          }));
          continue;
        }
        addAttachment(`${file.name}:${file.size}:${file.lastModified}`, file);
        continue;
      }
      const nativePath = getNativeFilePath(file);
      if (nativePath) {
        addAttachment(nativePath);
        continue;
      }

      const hostInputToken = await uploadHostInputAttachment(file);
      if (hostInputToken === HOST_INPUT_REJECTED) continue;
      if (hostInputToken) {
        addAttachment(hostInputToken, file);
        continue;
      }

      const stagedPath = await saveInlineFile(file);
      if (stagedPath) {
        addAttachment(stagedPath, file);
      }
    }
  }, [addAttachment, disabled, getNativeFilePath, isStreaming, saveInlineFile, selectedDesktopClientId, uploadHostInputAttachment]);

  // 暴露方法给父组件
  React.useImperativeHandle(ref, () => ({
    setValue: (newValue: string) => {
      setValue(newValue);
      // 触发自动调整高度
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
        }
      });
    },
    focus: () => {
      textareaRef.current?.focus();
    },
    addFiles: (files: FileList | File[]) => {
      void handleIncomingFiles(files);
    },
  }));

  const handleAddFile = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => {
      if (input.files) void handleIncomingFiles(input.files);
    };
    input.click();
  }, [handleIncomingFiles]);

  const handleRemoveAttachment = useCallback((path: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.path !== path));
  }, []);

  const hasFileTransfer = (dataTransfer: DataTransfer | null): boolean => {
    if (!dataTransfer) return false;
    if (dataTransfer.files.length > 0) return true;
    return Array.from(dataTransfer.types).includes('Files');
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (!disabled && !isStreaming) {
      setIsDraggingFiles(true);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled || isStreaming ? 'none' : 'copy';
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    // 标记原生事件已处理，避免 document 级监听器重复处理
    (event.nativeEvent as any)._boclawFileHandled = true;
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (disabled || isStreaming) return;
    void handleIncomingFiles(event.dataTransfer.files);
  };

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (disabled || isStreaming) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length === 0) return;
    event.preventDefault();
    void handleIncomingFiles(files);
  }, [disabled, handleIncomingFiles, isStreaming]);

  // Resolve the actual relay client for the selected desktop.
  // For the '__desktop__' sentinel (active session view), try session→client mapping first,
  // then fall back to the first available relay client (usually only one desktop is connected).
  const resolvedDesktopClient = (() => {
    if (!selectedDesktopClientId) return null;
    if (selectedDesktopClientId === '__desktop__') {
      const actualClientId = sessionId ? getTargetClient(sessionId) : null;
      if (actualClientId) {
        const found = relayClients.find(c => c.clientId === actualClientId);
        if (found) return found;
      }
      // Fallback: use the first available desktop client.
      return desktopRelayClients[0] ?? null;
    }
    return relayClients.find(c => c.clientId === selectedDesktopClientId) ?? null;
  })();

  // Desktop is offline when: relay has loaded, a desktop client is selected, but no online client found
  const desktopOffline = !!selectedDesktopClientId && relayReady && (
    resolvedDesktopClient ? !resolvedDesktopClient.online : true
  );

  // 工作目录选择只对「新建桌面端会话」开放：'__desktop__' 是查看已有桌面会话时用的哨兵，
  // 那种场景 cwd 由会话自身决定；父组件不传 onDesktopCwdChange 时也一并隐藏。
  const canPickDesktopCwd = !!onDesktopCwdChange
    && !!selectedDesktopClientId
    && selectedDesktopClientId !== '__desktop__';
  const desktopCwdClientId = resolvedDesktopClient?.clientId ?? selectedDesktopClientId;

  const sandboxReady = isQuickComposer || !showSandboxSelector || sandboxDisabled || !!selectedDesktopClientId || !!activeCloudEnv;
  const canSubmit = !submitting && !disabled && asrState === 'idle' && (!!value.trim() || attachments.length > 0) && sandboxReady && !desktopOffline;
  const asrButton = asrState === 'recording' ? (
    <button
      type="button"
      onClick={handleAsrClick}
      className={`${isLarge ? 'h-10 w-10' : 'h-8 w-8'} inline-flex shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-500 transition-all hover:bg-red-500/20 active:scale-95`}
      aria-label="停止录音"
      title="停止录音并转换为文字"
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-red-500/25" />
        <StopIcon className="relative h-3.5 w-3.5" />
      </span>
    </button>
  ) : (
    <button
      type="button"
      onClick={handleAsrClick}
      disabled={disabled || isStreaming || asrState !== 'idle'}
      className={`${isLarge ? 'h-10 w-10' : 'h-8 w-8'} inline-flex shrink-0 items-center justify-center rounded-full text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:cursor-not-allowed disabled:opacity-45 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText`}
      aria-label="语音输入"
      title={asrState === 'connecting' ? '正在连接语音识别服务' : asrState === 'finishing' ? '正在生成文字' : '语音输入'}
    >
      {asrState === 'connecting' || asrState === 'finishing' ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <MicrophoneIcon className={isLarge ? 'h-5 w-5' : 'h-4 w-4'} />
      )}
    </button>
  );

  // 附件卡片区（对齐桌面端）：大 composer 内嵌输入框顶部，小 composer 置于输入框上方
  const allImageAttachments = attachments.length > 0
    && attachments.every((a) => isCoworkImagePath(a.path) || Boolean(attachmentPreviews[a.path]));
  const attachmentsBlock = attachments.length > 0 ? (
    <div className={isLarge ? 'flex flex-wrap gap-2 px-3 pt-3' : 'mb-2 flex flex-wrap gap-2'}>
      {attachments.map((attachment) => (
        <CoworkAttachmentCard
          key={attachment.path}
          path={attachment.path}
          name={attachment.name}
          thumbnail={attachmentPreviews[attachment.path]}
          fileSize={attachment.size}
          compact={allImageAttachments}
          onRemove={handleRemoveAttachment}
        />
      ))}
    </div>
  ) : null;
  const enhancedContainerClass = isDraggingFiles
    ? `${containerClass} ring-2 ring-claude-accent/50 border-claude-accent/60`
    : containerClass;

  // 大小输入框复用同一执行策略入口，桌面会话由 Relay 获取并更新实际权限。
  const permissionSelector = !isQuickComposer && (selectedDesktopClientId
    ? <RelayCoworkPermissionModeSelector selectedDesktopClientId={selectedDesktopClientId} />
    : <CloudPermissionPolicy />);

  return (
    <div className="relative">
      {!isLarge && attachmentsBlock}
      <div
        className={enhancedContainerClass}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDraggingFiles && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-claude-accent/10 text-xs font-medium text-claude-accent">
            {i18nService.t('coworkDropFileHint')}
          </div>
        )}
        {isLarge && attachmentsBlock}
        {isLarge ? (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={`${isQuickComposer ? i18nService.t('coworkQuickPlaceholder') : placeholder} · ${i18nService.t('coworkInputShortcutHint').replace('{shortcut}', navigator.platform.toUpperCase().includes('MAC') ? 'Shift / ⌥ / ⌘+Enter' : 'Shift / Ctrl+Enter')}`}
              disabled={disabled}
              rows={isLarge ? 2 : 1}
              className={textareaClass}
              style={{ minHeight: `${minHeight}px` }}
            />
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-4 pb-2 pt-1.5">
              <div className="flex items-center gap-2 relative flex-wrap min-w-0">
                {permissionSelector}
                {/* + 统一工具菜单：添加文件 / 场景 / 技能（对齐桌面端布局） */}
                {!isQuickComposer && (
                  <div ref={plusRef} className="relative shrink-0">
                    <button
                      ref={plusBtnRef}
                      type="button"
                      onClick={() => {
                        setPlusOpen((v) => !v);
                        setPlusPanel(null);
                      }}
                      disabled={disabled}
                      className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all ${
                        plusOpen || plusPanel
                          ? 'border-claude-border bg-claude-surfaceHover text-claude-text dark:border-claude-darkBorder dark:bg-claude-darkSurfaceHover dark:text-claude-darkText'
                          : 'border-transparent text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
                      }`}
                      title={i18nService.t('coworkMoreTools')}
                      aria-label={i18nService.t('coworkMoreTools')}
                    >
                      <PlusIcon className={`h-4 w-4 transition-transform duration-200 ${plusOpen || plusPanel ? 'rotate-45' : ''}`} />
                    </button>
                    {plusOpen && (
                      <div className="absolute bottom-full left-0 z-50 mb-2 w-56 overflow-hidden rounded-xl border border-claude-border bg-white p-1.5 shadow-xl dark:border-claude-darkBorder dark:bg-claude-darkSurface">
                        <button
                          type="button"
                          onClick={() => { setPlusOpen(false); handleAddFile(); }}
                          disabled={isStreaming}
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-claude-text transition-colors hover:bg-claude-surfaceHover disabled:opacity-50 dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
                        >
                          <DocumentPlusIcon className="h-4 w-4 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                          <span className="flex-1 text-left">{i18nService.t('coworkAddFile')}</span>
                        </button>
                        {onSelectScenario && (
                          <button
                            type="button"
                            onClick={() => { setPlusOpen(false); setPlusPanel('scenario'); }}
                            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-claude-text transition-colors hover:bg-claude-surfaceHover dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
                          >
                            <Squares2X2Icon className="h-4 w-4 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                            <span className="flex-1 text-left">{i18nService.t('coworkToolScenario')}</span>
                            {activeScenario && <span className="max-w-20 truncate text-xs text-claude-accent">{activeScenario.name}</span>}
                            <ChevronRightIcon className="h-3.5 w-3.5 text-claude-textSecondary/60" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => { setPlusOpen(false); setPlusPanel('skills'); }}
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-claude-text transition-colors hover:bg-claude-surfaceHover dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
                        >
                          <CpuChipIcon className="h-4 w-4 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                          <span className="flex-1 text-left">{i18nService.t('skills')}</span>
                          <ChevronRightIcon className="h-3.5 w-3.5 text-claude-textSecondary/60" />
                        </button>
                      </div>
                    )}
                    {plusPanel === 'scenario' && onSelectScenario && (
                      <div className="absolute bottom-full left-0 z-50 mb-2">
                        <ScenarioPickerPanel
                          activeScenario={activeScenario}
                          onSelectScenario={setPreviewScenario}
                          onClose={() => setPlusPanel(null)}
                        />
                      </div>
                    )}
                    <SkillsPopover
                      isOpen={plusPanel === 'skills'}
                      onClose={() => setPlusPanel(null)}
                      onSelectSkill={(skill) => { setPlusPanel(null); handleSelectSkill(skill); }}
                      onManageSkills={() => { setPlusPanel(null); handleManageSkills(); }}
                      anchorRef={plusBtnRef as React.RefObject<HTMLElement>}
                    />
                  </div>
                )}
                {showSandboxSelector && (
                  <>
                    <div className="relative">
                      <button
                        ref={sandboxButtonRef}
                        type="button"
                        onClick={() => !sandboxDisabled && setShowSandboxMenu(!showSandboxMenu)}
                        disabled={sandboxDisabled}
                        title={i18nService.t('sandboxProfileSelectTitle')}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                          sandboxDisabled
                            ? 'opacity-50 cursor-not-allowed dark:text-claude-darkTextSecondary text-claude-textSecondary'
                            : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text'
                        }`}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                          <line x1="12" y1="22.08" x2="12" y2="12" />
                        </svg>
                        {selectedDesktopClientId && desktopOffline && (
                          <span className="relative flex-shrink-0 group/offline">
                            <svg className="h-3.5 w-3.5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover/offline:opacity-100 transition-opacity z-50">
                              {i18nService.t('relayDesktopOffline')}
                            </span>
                          </span>
                        )}
                        <span className="max-w-[150px] truncate text-xs">
                          {selectedDesktopClientId
                            ? (selectedDesktopClientName || resolvedDesktopClient?.deviceName || i18nService.t('envSelectorDesktopClient'))
                            : cloudEnvChipLabel}
                        </span>
                      </button>
                    </div>
                    {!sandboxDisabled && (
                      <ExecutionEnvPopover
                        isOpen={showSandboxMenu}
                        onClose={() => setShowSandboxMenu(false)}
                        anchorRef={sandboxButtonRef as React.RefObject<HTMLElement>}
                        profiles={sandboxProfiles}
                        images={sandboxImages}
                        selectedProfileId={selectedSandboxProfileId ?? null}
                        selectedImageId={selectedSandboxImageId ?? null}
                        onSelectCloud={(profileId, imageId) => {
                          onDesktopClientChange?.(null);
                          onSandboxProfileChange?.(profileId);
                          onSandboxImageChange?.(imageId);
                        }}
                        relayClients={desktopRelayClients}
                        selectedDesktopClientId={selectedDesktopClientId}
                        onSelectDesktop={(clientId) => {
                          onDesktopClientChange?.(clientId);
                          onSandboxProfileChange?.(null);
                        }}
                        onManage={onManageSandboxImages ?? onManageSandboxes}
                      />
                    )}
                  </>
                )}
                {/* 工作目录选择 —— 仅「新建桌面端会话」时可选：远程浏览桌面目录树，
                    选定后随 session.start 的 cwd 下发。已有会话（'__desktop__' 哨兵）
                    的工作目录由桌面端会话自身决定，不可改。 */}
                {showSandboxSelector && canPickDesktopCwd && (
                  <>
                    <button
                      type="button"
                      onClick={() => !sandboxDisabled && setShowCwdPicker(true)}
                      disabled={sandboxDisabled}
                      title={desktopCwd || i18nService.t('codeCwdTitle')}
                      className={`flex min-w-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                        sandboxDisabled
                          ? 'opacity-50 cursor-not-allowed dark:text-claude-darkTextSecondary text-claude-textSecondary'
                          : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text'
                      }`}
                    >
                      <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                      <span className="max-w-[140px] truncate text-xs">
                        {desktopCwd ? getFileNameFromPath(desktopCwd) : i18nService.t('codeCwdDefaultLabel')}
                      </span>
                    </button>
                    {showCwdPicker && desktopCwdClientId && (
                      <CwdPickerModal
                        clientId={desktopCwdClientId}
                        initialPath={desktopCwd}
                        onSelect={(picked) => {
                          onDesktopCwdChange?.(picked || null);
                          setShowCwdPicker(false);
                        }}
                        onClose={() => setShowCwdPicker(false)}
                      />
                    )}
                  </>
                )}
                {!isQuickComposer && <ActiveSkillBadge />}
              </div>
              <div className="flex items-center gap-2">
                {showModelSelector && <ModelSelector dropdownDirection="up" />}
                {asrButton}
                {isStreaming && !value.trim() ? (
                  <button
                    type="button"
                    onClick={handleStopClick}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-subtle transition-all hover:bg-red-600 hover:shadow-card active:scale-95"
                    aria-label="Stop"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-white shadow-subtle transition-all hover:shadow-card active:scale-95 disabled:cursor-not-allowed disabled:bg-claude-surfaceMuted disabled:text-claude-textSecondary/45 disabled:opacity-100 dark:disabled:bg-claude-darkSurfaceHover dark:disabled:text-claude-darkTextSecondary/45 ${
                      isStreaming ? 'bg-orange-500 hover:bg-orange-600' : 'bg-claude-accent hover:bg-claude-accentHover'
                    }`}
                    aria-label={isStreaming ? i18nService.t('coworkInterruptAndSend') : 'Send'}
                    title={isStreaming ? i18nService.t('coworkInterruptAndSend') : undefined}
                  >
                    <PaperAirplaneIcon className="h-[18px] w-[18px]" />
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              className={textareaClass}
            />

            <div className="flex items-center gap-1">
              {permissionSelector}
              <button
                type="button"
                onClick={handleAddFile}
                className="flex-shrink-0 p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text transition-colors"
                title={i18nService.t('coworkAddFile')}
                aria-label={i18nService.t('coworkAddFile')}
                disabled={disabled || isStreaming}
              >
                <AttachmentIcon className="h-4 w-4" />
              </button>
            </div>

            {asrButton}
            {isStreaming && !value.trim() ? (
              <button
                type="button"
                onClick={handleStopClick}
                className="flex-shrink-0 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all shadow-subtle hover:shadow-card active:scale-95"
                aria-label="Stop"
              >
                <StopIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex-shrink-0 p-2 rounded-lg text-white transition-all shadow-subtle hover:shadow-card active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isStreaming ? 'bg-orange-500 hover:bg-orange-600' : 'bg-claude-accent hover:bg-claude-accentHover'
                }`}
                aria-label={isStreaming ? i18nService.t('coworkInterruptAndSend') : 'Send'}
                title={isStreaming ? i18nService.t('coworkInterruptAndSend') : undefined}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>
      {previewScenario && onSelectScenario && (
        <ScenarioDetailDialog
          key={previewScenario.id}
          scenario={previewScenario}
          onClose={() => setPreviewScenario(null)}
          onUse={onSelectScenario}
          useLabel={sessionId && !sessionIsDraft ? i18nService.t('scenarioApplyToConversation') : undefined}
        />
      )}
      {lostAttachments > 0 && <p role="status" className="mt-2 text-xs text-amber-600">{i18nService.t('coworkDraftAttachmentsLost')}</p>}
      {isLarge && (
        <p className="mt-2 text-center text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {i18nService.t('coworkAiDisclaimer')}
        </p>
      )}
    </div>
  );
  }
);

ScopedPromptInput.displayName = 'ScopedPromptInput';
/** 按用户和会话重建输入状态，防止切换后的旧 effect 写入另一份草稿。 */
const CoworkPromptInput = React.forwardRef<CoworkPromptInputRef, CoworkPromptInputProps>((props, ref) => {
  const auth = useSyncExternalStore(
    (notify) => authService.subscribe(notify),
    () => authService.getAuthState(),
  );
  const draftKey = conversationDraftKey(authService.getBaseUrl(), String(auth?.user.id ?? 'anonymous'),
    props.sessionIsDraft ? undefined : props.sessionId, props.selectedDesktopClientId || 'cloud');
  return <ScopedPromptInput key={draftKey} {...props} draftKey={draftKey} ref={ref} />;
});
CoworkPromptInput.displayName = 'CoworkPromptInput';

export default CoworkPromptInput;
